import { isHabitCompletedOnDay } from '@griply/utils';
import {
  useCreateSubTask,
  useSubTasks,
  useUpdateSubtaskOrder,
  useUpdateTask,
} from 'features/tasks';
import React, { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useOptimisticData } from 'shared/hooks/use-optimistic-data';
import { useUser } from 'shared/hooks/use-user';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { mapTaskToTaskCard } from 'shared/utils/mappers/map-task-to-task-card';
import { orderTaskCardTasksByDate } from 'shared/utils/order-task-card-tasks-by-date';

import { ReorderableSubTaskList } from './reorderable-sub-task-list';

export type SubTasksListProps = {
  task: Task;
};

export const SubTasksList: React.FC<SubTasksListProps> = ({ task }) => {
  const subTasks = useSubTasks(task);
  const [optimisticSubTasks, addOptimisticSubTask] =
    useOptimisticData(subTasks);
  const { updateOrder } = useUpdateSubtaskOrder(task);
  const { submit: createSubTask } = useCreateSubTask({
    parentId: task.id,
    parentParentIds: task.parentIds ?? null,
    parentChildIds: task.childIds ?? null,
  });
  const { submit: updateTask } = useUpdateTask();

  const today = useToday();
  const user = useUser();

  const onReorder = (taskIds: ID[]) => !task.frozenAt && updateOrder(taskIds);

  const onCreateSubTask = async (name: string) => {
    if (task.frozenAt) {
      return;
    }
    const newSubTask = await createSubTask(name);
    if (newSubTask) {
      addOptimisticSubTask(newSubTask);
    }
  };

  const onUpdateTask = (changes: {
    id: ID;
    name?: string;
    completedAt?: Timestamp | null;
  }) => !task.frozenAt && updateTask(changes);

  const isCompletedHabit = useMemo(
    () => isHabitCompletedOnDay(task, today),
    [task, today],
  );

  const subTaskTaskCardTasks = useMemo(() => {
    const mappedSubTasks = optimisticSubTasks.map((subTask) =>
      mapTaskToTaskCard(
        {
          ...subTask,
          // if the main task is a completed habit, all sub-tasks should be displayed as completed as well,
          // whether they are completed or not
          completedAt: isCompletedHabit ? new Date() : subTask.completedAt,
        },
        {
          today,
          weekStartsOn: getWeekStartsOn(user),
        },
      ),
    );

    return orderTaskCardTasksByDate(mappedSubTasks, {
      null: task?.subtaskOrder ?? [],
    });
  }, [isCompletedHabit, optimisticSubTasks, task?.subtaskOrder, today, user]);

  return (
    <ReorderableSubTaskList
      tasks={subTaskTaskCardTasks}
      onReorder={onReorder}
      createSubTask={onCreateSubTask}
      updateSubTask={onUpdateTask}
      isDisabled={!!task.frozenAt}
    />
  );
};
